// Board FX (IMPLEMENTATION §3 M10, UX §5 "Feedback and juice per SimEvent", GDD §14), PURE: SimEvents →
// tweens on the views' anim records, FX sprite records and particles, on the FX clock. The Phaser layer
// (render/phaser/views/Fx.ts) only draws `sprites` and `particles.list()`; ItemViews / BuildingViews draw
// their anims. Everything is cosmetic: the views always show where the sim says things are.
//   merge   items converge 90 ms → additive white flash 0.5→1.6 (180 ms), ring shockwave, 8 + tier (≤ 14)
//           tier-coloured sparks, squash & stretch 1.3/0.8 → 1 (Back.out 220 ms); a new tier adds a 12-star
//           burst; Quick Merge / Merge All batches stagger 60 ms (the first 12 animated, the rest instant;
//           reduce motion: the batch lands at once with ONE static ring)
//   spawn   220 ms arc (16 px peak) from the vein (a machine's output pops out in outputPopMs), land squash
//           1.15/0.88; tier ≥ 3 sparkles · hit: vein squash 0.94 + 3–5 chips in the ore colour
//   bonk    wobble ±4° + dust · empty: the gun wiggles
//   insert  the item shrinks into the machine, the machine gulps · craft: press stamp + dust, packer
//           squeeze, loader chk-chk, caster pour glow, smelter embers (≤ 1/s); a new tier: stars
//   fire    gun recoil, muzzle star + puff at the orange tip, a casing tinkles out, the rail target pops
//           into confetti (up to 0.6 cell into the row above, the confetti within confettiOverflowCells of
//           the cell; ≤ 4 visible shots/s per Gallery, 8/s in all — one ShotGate decision shared with the DOM
//           coin) and respawns from below the rail (the coin flies to the HUD in the DOM layer, fx/DomFx)
//   stall   badge pop (Back.out 200 ms) · unstall: shrink (only stalls with a badge frame: stalls.json glyph)
//           · place / built / bmerge: pop, quake rings, debris;
//   bremove: dust · expand: rock cells crumble into 4 falling chunks + dust (capped)
// Reduce motion (UX §5 RM column; §3 M10 "0 scale tweens"): no squash / stretch / pops / rings / recoil /
// tilt / shake — alpha crossfades instead; flashes ≤ 0.3 alpha and never more than 3 per second; particles
// ×0.25. Caps: juice caps.tweens (the oldest settles), caps.particles (throttle at 80 %, refuse at 100 %;
// Low quality: caps.particlesLow, re-read at every batch). The optional camera shake (off in v0.1, N6) only
// follows an item merge of tier ≥ camera.shakeMinTier or a new tier.
import { CONFETTI_FRAMES, frameId } from '../../art/catalog.ts';
import type { Dir } from '../../sim/core/types.ts';
import type { SimEvent } from '../../sim/events.ts';
import { DEPTH } from '../layers.ts';
import type { JuiceCfg, PresetName } from './config.ts';
import { tierColor } from './config.ts';
import {
  barrelDeg,
  casterPour,
  type GeoCfg,
  gunPort,
  gunPose,
  onGun,
  partPose,
  stallPose,
  TARGET_SLOTS,
  targetPose,
} from './geometry.ts';
import { type Emit, Particles } from './particles.ts';
import { ShotGate } from './shotGate.ts';
import { type TweenCounts, type TweenSpec, Tweens } from './tweens.ts';

/** A view's animation record: offsets (world units), scale multipliers, alpha and angle (degrees). */
export interface Anim {
  dx: number;
  dy: number;
  sx: number;
  sy: number;
  a: number;
  rot: number;
}

export function restAnim(): Anim {
  return { dx: 0, dy: 0, sx: 1, sy: 1, a: 1, rot: 0 };
}

export function resetAnim(a: Anim): void {
  a.dx = 0;
  a.dy = 0;
  a.sx = 1;
  a.sy = 1;
  a.a = 1;
  a.rot = 0;
}

export interface ItemFx {
  anim: Anim;
  /** World centre without the anim offset. */
  x: number;
  y: number;
}

/** A view dropped this frame (an item merged / sold / inserted, a building removed): where and what it drew. */
export interface GoneFx {
  x: number;
  y: number;
  frame: string;
  hidden: boolean;
}

/** The anim records a building view keeps across frames (created on demand, dropped with the view). */
export interface BldAnims {
  body: Anim;
  /** Gallery: the mounted gun (recoil / wiggle) and the rail targets (pop / respawn). */
  gun: Anim;
  targets: Anim[];
  /** The Gallery's rail target kind follows the last fired ammo tier (BoardFx sets it; 0 = the queue's). */
  targetTier: number;
  stall: Anim;
  piston: Anim;
}

export function restBldAnims(): BldAnims {
  return {
    body: restAnim(),
    gun: restAnim(),
    targets: Array.from({ length: TARGET_SLOTS }, restAnim),
    targetTier: 0,
    stall: restAnim(),
    piston: restAnim(),
  };
}

export function resetBldAnims(b: BldAnims): void {
  resetAnim(b.body);
  resetAnim(b.gun);
  for (const t of b.targets) resetAnim(t);
  resetAnim(b.stall);
  resetAnim(b.piston);
}

export interface BldFx {
  /** The view's persistent anim records (tweens write into them). */
  a: BldAnims;
  type: string;
  level: number;
  dir: Dir;
  ore: string | null;
  x: number;
  y: number;
  gunTier: number | null;
  /** The stall badge share of the cell (§2.6 minimum applied). */
  stallShare: number;
}

export interface FxViews {
  item(id: number): ItemFx | null;
  gone(id: number): GoneFx | null;
  bld(bid: number): BldFx | null;
  goneBld(bid: number): GoneFx | null;
  /** World cells that became floor with expansion `id` (the crumble). */
  revealed(id: string): readonly { x: number; y: number }[];
  /** The mounted gun frame's muzzle socket centroid (cell units of the frame), or null. */
  muzzle(gunTier: number): { x: number; y: number } | null;
  /** The atlas has frame `id` (a stall without a badge glyph, e.g. NO_INPUT, has none). */
  hasFrame(id: string): boolean;
}

/** An FX sprite record: the Phaser layer draws `frame` at (x, y), `size·s·sx × size·s·sy`, alpha a. */
export interface FxSprite {
  frame: string;
  depth: number;
  x: number;
  y: number;
  size: number;
  s: number;
  sx: number;
  sy: number;
  a: number;
  rot: number;
  tint: number;
  add: boolean;
  gallery: boolean;
  done: boolean;
}

export interface BoardFxOptions {
  cfg: JuiceCfg;
  geo: GeoCfg;
  /** World units per cell. */
  wu: number;
  seed: number;
  /** Target kind per ammo tier (juice.json targets.byAmmoTier). */
  byAmmoTier: readonly string[];
  /** ui.json sprites.itemScaleMin (a spawn / placement grows from it). */
  spawnScale: number;
  insertEndScale: number;
  /** CSS px per cell (px-valued juice numbers → world units). */
  cellCss(): number;
  /** Settings (fx/motion.ts, fx/quality.ts): read at every event batch. */
  reduceMotion(): boolean;
  cameraShake(): boolean;
  lowQuality(): boolean;
  /** The visible-shot decision shared with the DOM FX (default: a gate of its own). */
  shots?: ShotGate;
}

export interface FxStats {
  particles: number;
  particlesPeak: number;
  particlesSkipped: number;
  particleCap: number;
  tweens: number;
  tweensPeak: number;
  tweensSettled: number;
  sprites: number;
  spritesPeak: number;
  started: TweenCounts;
  flashes: number;
  shots: { shown: number; skipped: number };
}

/** `__gm.render.fx().board`: the board FX counters (M10 caps) plus the FX clock. */
export interface BoardFxStats extends FxStats {
  /** Camera zoom tweens started (an expansion; reduce motion snaps: 0 — the spy beside `started.scale`). */
  cameraTweens: number;
  clockMs: number;
  frozen: boolean;
  instant: boolean;
}

const CONFETTI = Array.from({ length: CONFETTI_FRAMES }, (_, i) => frameId.fx(`confetti.${i}`));

/** Particle texture id of a preset's `frame` name (smoke is the baked `fx.puff`). */
export function particleFrame(name: string): string {
  return name === 'smoke' ? frameId.puff : frameId.fx(name);
}

type Rec = Record<string, number>;
const rec = (o: object): Rec => o as unknown as Rec;

export class BoardFx {
  readonly tweens: Tweens;
  readonly particles: Particles;
  private live: FxSprite[] = [];
  private later: { at: number; fn: () => void }[] = [];
  private readonly o: BoardFxOptions;
  private readonly c: JuiceCfg;
  private readonly shots: ShotGate;
  private shotSlot = new Map<number, number>();
  private flashesAt: number[] = [];
  private emberAt = new Map<number, number>();
  private stallShown = new Map<number, string>();
  private shakeFrom = Number.NEGATIVE_INFINITY;
  private spritesPeakN = 0;
  private flashN = 0;
  private shownN = 0;
  private skippedShotsN = 0;
  private rngState: number;
  private readonly seed: number;
  /** The clock time a scheduled callback runs at (its due time, not the frame's). */
  private atNow = 0;

  constructor(o: BoardFxOptions) {
    this.o = o;
    this.c = o.cfg;
    this.seed = o.seed;
    this.tweens = new Tweens(o.cfg.caps.tweens);
    this.shots =
      o.shots ??
      new ShotGate({
        perSec: o.cfg.caps.galleryShotsPerSec,
        totalPerSec: o.cfg.caps.galleryShotsTotalPerSec,
      });
    this.particles = new Particles({
      cap: this.particleCap(),
      throttleAt: o.cfg.caps.particleThrottleAt,
      seed: o.seed,
      frameOf: particleFrame,
    });
    this.rngState = (o.seed ^ 0x9e3779b9) >>> 0;
  }

  /** The particle cap of the current quality setting (juice caps.particles / particlesLow). */
  private particleCap(): number {
    return this.o.lowQuality() ? this.c.caps.particlesLow : this.c.caps.particles;
  }

  /** A deterministic [0, 1) draw for effect choices (the particles have their own stream). */
  private rand(): number {
    this.rngState = (this.rngState + 0x6d2b79f5) >>> 0;
    let t = this.rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  // ---- building blocks -----------------------------------------------------------------------------

  private tween(spec: TweenSpec, now: number): void {
    this.tweens.add(spec, now);
  }

  private at(t: number, fn: () => void): void {
    this.later.push({ at: t, fn });
  }

  /** CSS px → world units. */
  private px(n: number): number {
    const css = this.o.cellCss();
    return css > 0 ? (n * this.o.wu) / css : 0;
  }

  /** Particle count after reduce motion / low quality scaling (never below 1 for a non-zero request). */
  private n(k: number, rm: boolean): number {
    if (k <= 0) return 0;
    let f = 1;
    if (rm) f *= this.c.reduceMotion.particleScale;
    if (this.o.lowQuality()) f *= this.c.lowQuality.particleScale;
    return Math.max(1, Math.round(k * f));
  }

  private burst(name: PresetName, k: number, at: Omit<Emit, 'wu'>, now: number): void {
    if (k <= 0) return;
    this.particles.emit(this.c.presets[name], k, { ...at, wu: this.o.wu }, now);
  }

  private sprite(
    frame: string,
    x: number,
    y: number,
    size: number,
    now: number,
    to: Partial<Pick<FxSprite, 'x' | 'y' | 's' | 'sx' | 'sy' | 'a' | 'rot'>>,
    ms: number,
    opts: {
      from?: Partial<Pick<FxSprite, 's' | 'a' | 'rot' | 'sx' | 'sy'>>;
      depth?: number;
      tint?: number;
      add?: boolean;
      gallery?: boolean;
      ease?: (t: number) => number;
      delay?: number;
    } = {},
  ): FxSprite {
    const f: FxSprite = {
      frame,
      depth: opts.depth ?? DEPTH.boardFx,
      x,
      y,
      size,
      s: 1,
      sx: 1,
      sy: 1,
      a: 1,
      rot: 0,
      tint: opts.tint ?? -1,
      add: opts.add ?? false,
      gallery: opts.gallery ?? false,
      done: false,
      ...opts.from,
    };
    // a delayed sprite stays invisible until its tween starts (then `from` applies)
    if (opts.delay !== undefined && opts.delay > 0) f.a = 0;
    this.live.push(f);
    this.spritesPeakN = Math.max(this.spritesPeakN, this.live.length);
    this.tween(
      {
        target: rec(f),
        to: to as Rec,
        ...(opts.from ? { from: { ...(opts.from as Rec) } } : {}),
        ms,
        ...(opts.delay !== undefined ? { delay: opts.delay } : {}),
        ease: opts.ease ?? this.c.ease.out,
        onDone: () => {
          f.done = true;
        },
      },
      now,
    );
    return f;
  }

  /** A flash: additive dot; under reduce motion a static ≤ 0.3-alpha fade, at most 3 per second. */
  private flash(
    x: number,
    y: number,
    now: number,
    from: number,
    to: number,
    ms: number,
    alpha: number,
    rm: boolean,
    tint = -1,
    depth: number = DEPTH.boardFx,
  ): void {
    const rmc = this.c.reduceMotion;
    if (rm) {
      this.flashesAt = this.flashesAt.filter((t) => now - t < 1000);
      if (this.flashesAt.length >= rmc.flashesPerSecMax) return;
      this.flashesAt.push(now);
      this.flashN += 1;
      this.sprite(frameId.fx('dot'), x, y, this.o.wu * to, now, { a: 0 }, ms, {
        from: { a: Math.min(alpha, rmc.flashAlphaMax) },
        add: true,
        tint,
        depth,
      });
      return;
    }
    this.flashN += 1;
    this.sprite(frameId.fx('dot'), x, y, this.o.wu, now, { s: to, a: 0 }, ms, {
      from: { s: from, a: alpha },
      add: true,
      tint,
      depth,
    });
  }

  private ring(
    x: number,
    y: number,
    now: number,
    from: number,
    to: number,
    ms: number,
    alpha: number,
    tint: number,
    delay = 0,
  ): void {
    this.sprite(frameId.fx('ring'), x, y, this.o.wu, now, { s: to, a: 0 }, ms, {
      from: { s: from, a: alpha },
      tint,
      delay,
    });
  }

  private squash(anim: Anim, sx: number, sy: number, ms: number, now: number, delay = 0): void {
    this.tween(
      {
        target: rec(anim),
        from: { sx, sy },
        to: { sx: 1, sy: 1 },
        ms,
        delay,
        ease: this.c.ease.pop,
      },
      now,
    );
  }

  private fadeIn(anim: Anim, ms: number, now: number, delay = 0): void {
    this.tween(
      { target: rec(anim), from: { a: 0 }, to: { a: 1 }, ms, delay, ease: this.c.ease.linear },
      now,
    );
  }

  /** A damped rotation wobble of `anim` (±deg, `cycles` over `ms`). */
  private wobble(anim: Anim, deg: number, cycles: number, ms: number, now: number): void {
    const helper = { u: 0 };
    this.tween(
      {
        target: helper,
        to: { u: 1 },
        ms,
        ease: this.c.ease.linear,
        kind: 'rot',
        onStep: (_k, t) => {
          anim.rot = t >= 1 ? 0 : deg * Math.sin(2 * Math.PI * cycles * t) * (1 - t);
        },
      },
      now,
    );
  }

  private startShake(now: number): void {
    if (this.o.cameraShake()) this.shakeFrom = now;
  }

  // ---- events --------------------------------------------------------------------------------------

  /** Starts the effects of one event batch at clock time `now` (the views already show the new state). */
  handle(events: readonly SimEvent[], v: FxViews, now: number): void {
    const rm = this.o.reduceMotion();
    const ev = this.c.ev;
    this.particles.setCap(this.particleCap()); // Low quality may change live (M7 Settings)
    // pre-scan: discoveries (a star burst where the new item appears) and spawn sources (an ore spawned
    // straight into an acceptor flies in from its vein)
    const fresh = new Set<string>();
    const spawnedFrom = new Map<number, number>();
    for (const e of events) {
      if (e.e === 'discover') fresh.add(`${e.fam}:${e.tier}`);
      if (e.e === 'spawn') spawnedFrom.set(e.id, e.from);
    }
    let batch = 0;
    for (const e of events) {
      switch (e.e) {
        case 'spawn': {
          const it = v.item(e.id);
          if (it === null) break; // straight into an acceptor: the insert flies it in
          const src = e.from > 0 ? v.bld(e.from) : null;
          this.spawnFx(it, src, e.tier, rm, now);
          if (fresh.delete(`${e.fam}:${e.tier}`) && !rm) this.stars(it.x, it.y, now, rm);
          break;
        }
        case 'merge': {
          if (e.via === 'machine') break;
          const it = v.item(e.id);
          if (it === null) break;
          let delay = 0;
          if (e.via === 'quick' || e.via === 'all') {
            const i = batch++;
            // reduce motion (UX §5 QuickMerge / MergeAll "instant plus 1 ring"): the batch lands at once
            if (rm) {
              if (i === 0) this.batchRing(it, e.tier, now);
              break;
            }
            if (i >= ev.quickMerge.animatedMax) break; // the rest are instant
            delay = i * ev.quickMerge.staggerMs;
          }
          this.mergeFx(e, it, v, rm, now, delay, fresh.delete(`${e.fam}:${e.tier}`) || e.first);
          break;
        }
        case 'insert':
        case 'deliver':
          this.insertFx(e, v, spawnedFrom, rm, now);
          break;
        case 'hit': {
          const b = v.bld(e.bid);
          if (b === null) break;
          if (!rm) this.squash(b.a.body, ev.hit.squash, ev.hit.squash, ev.hit.squashMs, now);
          const n = rm
            ? ev.hit.reducedChips
            : ev.hit.chipsMin + Math.floor(this.rand() * (ev.hit.chipsMax - ev.hit.chipsMin + 1));
          const by = e.src === 'drill' ? v.bld(e.by) : null;
          const x = by !== null ? (b.x + by.x) / 2 : b.x;
          const y = by !== null ? (b.y + by.y) / 2 : b.y - this.o.wu * ev.hit.chipLiftCells;
          this.burst(
            'chip',
            rm ? n : this.n(n, rm),
            {
              x,
              y,
              depth: DEPTH.boardFx,
              tint: this.c.color.material[b.ore ?? ''] ?? this.c.color.rock,
            },
            now,
          );
          break;
        }
        case 'bonk': {
          const b = v.bld(e.bid);
          if (b === null) break;
          if (rm) {
            this.flash(b.x, b.y, now, 1, 1, ev.blocked.wobbleMs, 1, rm, this.c.color.invalid);
            break;
          }
          this.wobble(
            b.a.body,
            ev.blocked.wobbleDeg,
            ev.blocked.wobbleCycles,
            ev.blocked.wobbleMs,
            now,
          );
          this.burst(
            'dust',
            this.n(ev.blocked.dust, rm),
            { x: b.x, y: b.y, depth: DEPTH.boardFx },
            now,
          );
          break;
        }
        case 'craft': {
          const b = v.bld(e.bid);
          if (b === null) break;
          this.craftFx(e.bid, b, rm, now);
          if (fresh.delete(`${e.fam}:${e.tier}`) && !rm) this.stars(b.x, b.y, now, rm);
          break;
        }
        case 'fire':
          this.fireFx(e, v, rm, now);
          break;
        case 'empty': {
          const b = v.bld(e.bid);
          if (b === null || rm) break;
          const w = ev.empty;
          this.wobble(b.a.gun, w.wiggleDeg, w.wiggleCycles, w.wiggleMs, now);
          break;
        }
        case 'stall': {
          const b = v.bld(e.bid);
          const frame = frameId.stallBadge(e.stall);
          // a stall without a badge (stalls.json glyph null, e.g. NO_INPUT) has nothing to pop or shrink
          if (b === null || !v.hasFrame(frame)) break;
          this.stallShown.set(e.bid, frame);
          if (!rm) this.squash(b.a.stall, 0, 0, ev.stall.popMs, now);
          break;
        }
        case 'unstall': {
          const b = v.bld(e.bid);
          const frame = this.stallShown.get(e.bid);
          this.stallShown.delete(e.bid);
          if (b === null || frame === undefined) break;
          const p = stallPose(b.x, b.y, this.o.wu, b.stallShare);
          this.sprite(frame, p.x, p.y, p.w, now, rm ? { a: 0 } : { s: 0 }, ev.stall.clearMs, {
            depth: DEPTH.stall,
            ease: this.c.ease.inQuad,
          });
          break;
        }
        case 'place':
        case 'built': {
          const b = v.bld(e.bid);
          if (b === null) break;
          if (rm) {
            this.fadeIn(b.a.body, ev.ghost.buildMs, now);
            break;
          }
          if (e.e === 'place')
            this.squash(b.a.body, this.o.spawnScale, this.o.spawnScale, ev.ghost.buildMs, now);
          else {
            const k = ev.ghost.builtSquash;
            this.squash(b.a.body, k, k, ev.ghost.buildMs, now);
            this.burst(
              'dust',
              this.n(ev.ghost.puff, rm),
              { x: b.x, y: b.y + this.o.wu * ev.ghost.puffDropCells, depth: DEPTH.boardFx },
              now,
            );
          }
          break;
        }
        case 'bmerge':
          if (e.other > 0) this.forget(e.other); // merged away: its per-building state goes
          this.bmergeFx(e.bid, v, rm, now);
          break;
        case 'bremove': {
          this.forget(e.bid);
          const g = v.goneBld(e.bid);
          if (g === null) break;
          this.burst(
            'dust',
            this.n(ev.ghost.puff, rm),
            { x: g.x, y: g.y + this.o.wu * ev.ghost.puffDropCells, depth: DEPTH.boardFx },
            now,
          );
          break;
        }
        case 'saleUndone':
          for (const it of e.items) {
            const view = v.item(it.id);
            if (view === null) continue;
            if (rm) this.fadeIn(view.anim, ev.saleUndone.popMs, now);
            else this.squash(view.anim, 0, 0, ev.saleUndone.popMs, now);
          }
          break;
        case 'expand':
          this.crumble(v.revealed(e.id), rm, now);
          break;
        default:
          break;
      }
    }
  }

  /** A building left the board: its shot window, target slot, ember time and stall badge go. */
  private forget(bid: number): void {
    this.shots.forget(bid);
    this.shotSlot.delete(bid);
    this.emberAt.delete(bid);
    this.stallShown.delete(bid);
  }

  /** Reduce motion: ONE static ring (alpha only, ≤ the RM flash alpha) for a Quick Merge / Merge All batch. */
  private batchRing(it: ItemFx, tier: number, now: number): void {
    const m = this.c.ev.merge;
    this.sprite(
      frameId.fx('ring'),
      it.x,
      it.y,
      this.o.wu * m.ringTo,
      now,
      { a: 0 },
      m.reducedMs * 2,
      {
        from: { a: Math.min(m.ringAlpha, this.c.reduceMotion.flashAlphaMax) },
        tint: tierColor(this.c, tier),
      },
    );
  }

  private spawnFx(it: ItemFx, src: BldFx | null, tier: number, rm: boolean, now: number): void {
    const ev = this.c.ev.spawn;
    const anim = it.anim;
    if (src === null) {
      // a debug / overflow spawn pops in place
      if (rm) this.fadeIn(anim, ev.reducedMs, now);
      else this.squash(anim, this.o.spawnScale, this.o.spawnScale, ev.arcMs, now);
      return;
    }
    const dx = src.x - it.x;
    const dy = src.y - it.y;
    if (rm) {
      // a 120 ms slide, no squash
      this.tween(
        {
          target: rec(anim),
          from: { dx, dy },
          to: { dx: 0, dy: 0 },
          ms: ev.reducedMs,
          ease: this.c.ease.out,
        },
        now,
      );
      return;
    }
    // ore arcs out of its vein; a machine's output pops out of its front (UX §5 MachineCycle)
    const ms = src.type === 'deposit' ? ev.arcMs : this.c.ev.machine.outputPopMs;
    const peak = this.px(ev.arcPeakPx);
    const helper = { u: 0 };
    anim.dx = dx;
    anim.dy = dy;
    this.tween(
      {
        target: helper,
        to: { u: 1 },
        ms,
        ease: this.c.ease.out,
        kind: 'move',
        onStep: (k, t) => {
          anim.dx = dx * (1 - k);
          anim.dy = dy * (1 - k) - peak * 4 * t * (1 - t);
        },
      },
      now,
    );
    this.tween(
      {
        target: rec(anim),
        from: { sx: this.o.spawnScale, sy: this.o.spawnScale },
        to: { sx: 1, sy: 1 },
        ms,
        ease: this.c.ease.out,
      },
      now,
    );
    this.squash(anim, ev.landSquashX, ev.landSquashY, ev.landMs, now, ms);
    if (tier >= ev.sparkleMinTier)
      this.burst(
        'sparkle',
        this.n(ev.sparkles, rm),
        {
          x: it.x,
          y: it.y,
          depth: DEPTH.boardFx,
          tint: tierColor(this.c, tier),
          delayMs: ms,
        },
        now,
      );
  }

  private mergeFx(
    e: Extract<SimEvent, { e: 'merge' }>,
    it: ItemFx,
    v: FxViews,
    rm: boolean,
    now: number,
    delay: number,
    newTier: boolean,
  ): void {
    const ev = this.c.ev.merge;
    const anim = it.anim;
    const wu = this.o.wu;
    const tint = tierColor(this.c, e.tier);
    if (rm) {
      // a 120 ms crossfade: the two inputs fade out where they were, the result fades in
      for (const id of [e.a, e.b]) {
        const g = v.gone(id);
        if (g === null || g.hidden) continue;
        this.sprite(g.frame, g.x, g.y, wu, now, { a: 0 }, ev.reducedMs, { delay, from: { a: 1 } });
      }
      anim.a = 0;
      this.fadeIn(anim, ev.reducedMs, now, delay);
      this.at(now + delay, () => {
        const t = this.atNow;
        this.flash(it.x, it.y, t, 1, 1, ev.flashMs, ev.flashAlpha, true);
        this.burst('spark', ev.reducedSparks, { x: it.x, y: it.y, depth: DEPTH.boardFx, tint }, t);
      });
      return;
    }
    // converge: the inputs slide into the target cell (an input already on it stays), the result appears
    // when they meet; a staggered (Quick Merge / Merge All) pair waits in place for its turn
    const shown: GoneFx[] = [];
    let converge = 0;
    for (const id of [e.a, e.b]) {
      const g = v.gone(id);
      if (g === null || g.hidden) continue;
      shown.push(g);
      if (Math.abs(g.x - it.x) + Math.abs(g.y - it.y) >= wu * ev.convergeMinCells)
        converge = ev.convergeMs;
    }
    const hold = delay + converge;
    if (hold > 0) {
      const wait = delay / hold;
      const inQuad = this.c.ease.inQuad;
      const ease = (t: number): number => (t <= wait ? 0 : inQuad((t - wait) / (1 - wait)));
      for (const g of shown)
        this.sprite(g.frame, g.x, g.y, wu, now, { x: it.x, y: it.y }, hold, {
          ease,
          from: { a: 1 },
        });
    }
    const t0 = delay + converge;
    if (t0 > 0) {
      anim.a = 0;
      this.tween(
        { target: rec(anim), to: { a: 1 }, ms: 1, delay: t0 - 1, ease: this.c.ease.linear },
        now,
      );
    }
    this.squash(anim, ev.squashX, ev.squashY, ev.squashMs, now, t0);
    this.at(now + t0, () => {
      const t = this.atNow;
      this.flash(it.x, it.y, t, ev.flashFrom, ev.flashTo, ev.flashMs, ev.flashAlpha, false);
      this.ring(it.x, it.y, t, ev.ringFrom, ev.ringTo, ev.ringMs, ev.ringAlpha, tint);
      const sparks = Math.min(ev.sparksMax, ev.sparksBase + e.tier);
      this.burst(
        'spark',
        this.n(sparks, false),
        { x: it.x, y: it.y, depth: DEPTH.boardFx, tint },
        t,
      );
      if (newTier) this.stars(it.x, it.y, t, false);
      if (newTier || e.tier >= this.c.camera.shakeMinTier) this.startShake(t);
    });
  }

  private stars(x: number, y: number, now: number, rm: boolean): void {
    this.burst(
      'star',
      this.n(this.c.ev.discover.stars, rm),
      { x, y, depth: DEPTH.boardFx + 1, tint: this.c.color.glow },
      now,
    );
  }

  private insertFx(
    e: Extract<SimEvent, { e: 'insert' | 'deliver' }>,
    v: FxViews,
    spawnedFrom: ReadonlyMap<number, number>,
    rm: boolean,
    now: number,
  ): void {
    const ev = this.c.ev.insert;
    const b = v.bld(e.bid);
    if (b === null) return;
    const g = v.gone(e.id);
    const frame = e.e === 'insert' ? frameId.item(e.fam, e.tier) : (g?.frame ?? null);
    if (frame === null) return;
    const from = spawnedFrom.get(e.id);
    // where it came from: its last drawn view (a floor item, a chute carry at the chute's front edge), else
    // its vein (a spawn straight into an acceptor); a hand drop (its view hidden under the DOM ghost) is
    // swallowed where it lands. A machine's push into its neighbour has neither: the neighbour only gulps
    // (a full-size item over every busy machine would be noise).
    const src =
      (g !== null && !g.hidden ? g : null) ?? (from !== undefined && from > 0 ? v.bld(from) : null);
    const byHand = e.e === 'deliver' || e.hand === 1;
    if (src === null && !byHand) {
      if (!rm) this.squash(b.a.body, ev.gulpScale, ev.gulpScale, ev.gulpMs, now);
      return;
    }
    const size = this.o.wu;
    if (rm) {
      this.sprite(frame, b.x, b.y, size, now, { a: 0 }, ev.shrinkMs, {
        from: { s: this.o.spawnScale },
      });
      return;
    }
    const start = src ?? b;
    this.sprite(
      frame,
      start.x,
      start.y,
      size,
      now,
      { x: b.x, y: b.y, s: this.o.insertEndScale, a: ev.endAlpha },
      ev.shrinkMs,
      { from: { s: g !== null ? 1 : this.o.spawnScale }, ease: this.c.ease.inQuad },
    );
    this.squash(b.a.body, ev.gulpScale, ev.gulpScale, ev.gulpMs, now, ev.shrinkMs);
  }

  private craftFx(bid: number, b: BldFx, rm: boolean, now: number): void {
    if (rm) return; // RM: sprite swaps only
    const m = this.c.ev.machine;
    const wu = this.o.wu;
    switch (b.type) {
      case 'press': {
        // stamp down, then back up (a delayed tween: a clear() settles it at rest, never half-way)
        const p = rec(b.a.piston);
        const travel = m.pressTravel * wu;
        this.tween(
          { target: p, to: { dy: travel }, ms: m.pressDownMs, ease: this.c.ease.inQuad },
          now,
        );
        this.tween(
          {
            target: p,
            to: { dy: 0 },
            ms: m.pressUpMs,
            delay: m.pressDownMs,
            ease: this.c.ease.out,
          },
          now,
        );
        const pose = partPose('press', 'piston', b.x, b.y, wu);
        const y = pose ? pose.y + pose.h / 2 + travel : b.y;
        const dust = this.n(m.pressDust, false);
        this.at(now + m.pressDownMs, () =>
          this.burst('dust', dust, { x: b.x, y, depth: DEPTH.boardFx }, this.atNow),
        );
        break;
      }
      case 'packer': {
        const body = rec(b.a.body);
        const half = m.packerMs / 2;
        this.tween(
          {
            target: body,
            to: { sx: m.packerOvershoot, sy: m.packerSqueeze },
            ms: half,
            ease: this.c.ease.out,
          },
          now,
        );
        this.tween(
          { target: body, to: { sx: 1, sy: 1 }, ms: half, delay: half, ease: this.c.ease.pop },
          now,
        );
        break;
      }
      case 'loader':
      case 'gunsmith':
        // "chk-chk": two quick vertical squeezes
        this.squash(b.a.body, 1, m.loaderChkScale, m.loaderChkMs, now);
        this.squash(b.a.body, 1, m.loaderChkScale, m.loaderChkMs, now, m.loaderChkMs);
        break;
      case 'caster': {
        const p = casterPour(b.x, b.y, wu);
        this.flash(
          p.x,
          p.y,
          now,
          m.casterGlowSize,
          m.casterGlowSize,
          m.casterGlowMs,
          m.casterGlowAlpha,
          false,
          this.c.color.glow,
        );
        break;
      }
      case 'smelter': {
        const last = this.emberAt.get(bid) ?? Number.NEGATIVE_INFINITY;
        if (now - last < 1000 / m.embersPerSec) break;
        this.emberAt.set(bid, now);
        const pose = partPose('smelter', 'glow', b.x, b.y, wu);
        this.burst(
          'ember',
          1,
          { x: pose?.x ?? b.x, y: pose?.y ?? b.y, depth: DEPTH.boardFx, tint: this.c.color.glow },
          now,
        );
        break;
      }
      default:
        break;
    }
  }

  private bmergeFx(bid: number, v: FxViews, rm: boolean, now: number): void {
    const b = v.bld(bid);
    if (b === null) return;
    const bm = this.c.ev.buildingMerge;
    if (rm) {
      this.flash(b.x, b.y, now, 1, 1, bm.ringMs, 1, rm);
      return;
    }
    this.squash(b.a.body, bm.popScale, bm.popScale, bm.ringMs, now);
    const tint =
      b.type === 'deposit'
        ? (this.c.color.material[b.ore ?? ''] ?? this.c.color.rock)
        : this.c.color.glow;
    // the quake: rings from the building's foot, rocks bounce out
    const y = b.y + this.o.wu * bm.ringDropCells;
    for (let i = 0; i < bm.rings; i++)
      this.ring(
        b.x,
        y,
        now,
        bm.ringFrom,
        bm.ringTo,
        bm.ringMs,
        this.c.ev.merge.ringAlpha,
        tint,
        i * bm.ringGapMs,
      );
    this.burst(
      'debris',
      this.n(bm.debris, rm),
      {
        x: b.x,
        y: b.y,
        depth: DEPTH.boardFx,
        tint: b.type === 'deposit' ? tint : this.c.color.rock,
      },
      now,
    );
  }

  private fireFx(e: Extract<SimEvent, { e: 'fire' }>, v: FxViews, rm: boolean, now: number): void {
    const b = v.bld(e.bid);
    if (b === null) return;
    b.a.targetTier = e.ammoTier;
    // visible shots: ≤ caps.galleryShotsPerSec per Gallery, galleryShotsTotalPerSec in all (shared decision)
    if (!this.shots.visible(e, now)) {
      this.skippedShotsN += 1;
      return;
    }
    this.shownN += 1;
    const g = this.c.ev.galleryFire;
    const wu = this.o.wu;
    const slot = (this.shotSlot.get(e.bid) ?? 0) % TARGET_SLOTS;
    this.shotSlot.set(e.bid, slot + 1);
    const target = b.a.targets[slot];
    const tp = targetPose(b.x, b.y, wu, this.o.geo, slot);
    const last = this.o.byAmmoTier.length - 1;
    const kind = this.o.byAmmoTier[Math.max(0, Math.min(last, e.ammoTier - 1))] ?? 'bottle';
    if (rm) {
      // the target flips (an alpha crossfade); no recoil, puff or confetti
      if (target) {
        const half = g.flipMs / 2;
        const tr = rec(target);
        this.tween({ target: tr, to: { a: 0 }, ms: half, ease: this.c.ease.linear }, now);
        this.tween(
          { target: tr, to: { a: 1 }, ms: half, delay: half, ease: this.c.ease.linear },
          now,
        );
      }
      return;
    }
    // recoil along the barrel
    const pose = gunPose(b.x, b.y, wu, this.o.geo);
    const deg = barrelDeg(this.o.geo);
    const rad = (deg * Math.PI) / 180;
    const back = this.px(g.recoilPx);
    const gun = rec(b.a.gun);
    this.tween(
      {
        target: gun,
        to: { dx: -Math.cos(rad) * back, dy: -Math.sin(rad) * back },
        ms: g.recoilOutMs,
        ease: this.c.ease.out,
      },
      now,
    );
    this.tween(
      {
        target: gun,
        to: { dx: 0, dy: 0 },
        ms: g.recoilBackMs,
        delay: g.recoilOutMs,
        ease: this.c.ease.out,
      },
      now,
    );
    // muzzle star + puff at the orange tip
    const sock = b.gunTier !== null ? v.muzzle(b.gunTier) : null;
    const tip = sock !== null ? onGun(pose, sock.x, sock.y) : { x: pose.x, y: pose.y };
    this.sprite(
      frameId.fx('star4'),
      tip.x,
      tip.y,
      g.muzzleSize * wu,
      now,
      { s: g.muzzleTo, a: 0 },
      g.muzzleMs,
      {
        from: { s: g.muzzleFrom, a: 1, rot: this.rand() * g.muzzleSpinDeg },
        add: true,
        depth: DEPTH.galleryFx + 1,
        gallery: true,
        tint: this.c.color.glow,
      },
    );
    this.burst(
      'puff',
      this.n(g.puffs, rm),
      { x: tip.x, y: tip.y, depth: DEPTH.galleryFx, turnDeg: deg },
      now,
    );
    // a casing tinkles out of the ejection port
    const port = gunPort(pose);
    this.burst(
      'casing',
      g.casings,
      {
        x: port.x,
        y: port.y,
        depth: DEPTH.galleryFx,
        tint: this.c.color.material.casing ?? this.c.color.glow,
      },
      now,
    );
    // the rail target pops into confetti, then respawns from below the rail
    this.sprite(
      frameId.target(kind),
      tp.x,
      tp.y,
      tp.w,
      now,
      { y: tp.y - g.popUpCells * wu, a: 0, rot: (this.rand() - 0.5) * g.targetSpinDeg },
      g.targetRespawnMs,
      { depth: DEPTH.galleryFx, gallery: true, from: { a: 1 } },
    );
    // confetti may overflow the Gallery cell by confettiOverflowCells (§2.6), never further
    const over = g.confettiOverflowCells * wu;
    this.burst(
      'confetti',
      this.n(g.confetti, rm),
      {
        x: tp.x,
        y: tp.y,
        depth: DEPTH.galleryFx,
        frames: CONFETTI,
        bounds: {
          left: b.x - wu / 2 - over,
          right: b.x + wu / 2 + over,
          top: b.y - wu / 2 - over,
        },
      },
      now,
    );
    if (target) {
      target.a = 0;
      target.dy = g.targetRiseCells * wu;
      this.tween(
        {
          target: rec(target),
          to: { dy: 0, a: 1 },
          ms: g.targetRiseMs,
          delay: g.targetRespawnMs,
          ease: this.c.ease.pop,
        },
        now,
      );
    }
  }

  private crumble(cells: readonly { x: number; y: number }[], rm: boolean, now: number): void {
    const x = this.c.ev.expansion;
    const wu = this.o.wu;
    let dust = Math.min(x.dustMax, cells.length * x.dustPerCell);
    const q = Math.max(1, Math.round(Math.sqrt(x.chunks)));
    for (const c of cells) {
      const cx = (c.x + 0.5) * wu;
      const cy = (c.y + 0.5) * wu;
      for (let i = 0; i < x.chunks; i++) {
        const ox = ((i % q) + 0.5) / q - 0.5;
        const oy = (Math.floor(i / q) + 0.5) / q - 0.5;
        const px = cx + ox * wu;
        const py = cy + oy * wu;
        if (rm) {
          this.sprite(frameId.rock, px, py, wu / q, now, { a: 0 }, x.reducedMs, {
            depth: DEPTH.boardFx,
          });
          continue;
        }
        this.sprite(
          frameId.rock,
          px,
          py,
          wu / q,
          now,
          {
            y: py + x.chunkFallCells * wu,
            a: 0,
            rot: (this.rand() - 0.5) * x.chunkSpinDeg,
            s: x.chunkEndScale,
          },
          x.crumbleMs,
          { depth: DEPTH.boardFx, ease: this.c.ease.inQuad },
        );
      }
      const k = Math.min(dust, x.dustPerCell);
      dust -= k;
      if (k > 0) this.burst('dust', this.n(k, rm), { x: cx, y: cy, depth: DEPTH.boardFx }, now);
    }
  }

  // ---- clock ---------------------------------------------------------------------------------------

  /** Advances every tween, scheduled effect and particle to clock time `now`; drops finished sprites. */
  step(now: number): void {
    // scheduled effects run at their due time (a frame may be later), in due order
    if (this.later.length > 0) {
      const due = this.later.filter((l) => l.at <= now);
      if (due.length > 0) {
        this.later = this.later.filter((l) => l.at > now);
        due.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
        for (const l of due) {
          this.atNow = l.at;
          l.fn();
        }
      }
    }
    this.tweens.step(now);
    this.particles.step(now);
    if (this.live.some((f) => f.done)) this.live = this.live.filter((f) => !f.done);
  }

  /** Camera shake offset (world units) at clock time `now`; zero unless the setting is on. */
  shakeOffset(now: number): { x: number; y: number } {
    const cam = this.c.camera;
    const t = now - this.shakeFrom;
    if (!(t >= 0 && t < cam.shakeMs) || !this.o.cameraShake()) return { x: 0, y: 0 };
    const u = t / cam.shakeMs;
    const a = this.px(cam.shakePx) * (1 - u);
    return {
      x: a * Math.sin(u * Math.PI * 2 * cam.shakeWavesX),
      y: a * Math.cos(u * Math.PI * 2 * cam.shakeWavesY),
    };
  }

  get sprites(): readonly FxSprite[] {
    return this.live;
  }

  /** Something cosmetic is still moving (`__gm.idle`). */
  busy(): boolean {
    return (
      this.live.length > 0 ||
      this.particles.alive > 0 ||
      this.tweens.active > 0 ||
      this.later.length > 0
    );
  }

  /** Ends everything: tweens settle (anims back to rest values), sprites / particles / schedules drop. */
  clear(): void {
    this.later = [];
    this.tweens.settleAll();
    this.live = [];
    this.particles.clear();
    this.particles.reseed();
    this.rngState = (this.seed ^ 0x9e3779b9) >>> 0;
    this.shots.clear();
    this.shotSlot.clear();
    this.flashesAt = [];
    this.emberAt.clear();
    this.stallShown.clear();
    this.shakeFrom = Number.NEGATIVE_INFINITY;
  }

  stats(): FxStats {
    return {
      particles: this.particles.alive,
      particlesPeak: this.particles.peak,
      particlesSkipped: this.particles.skipped,
      particleCap: this.particles.cap,
      tweens: this.tweens.active,
      tweensPeak: this.tweens.peak,
      tweensSettled: this.tweens.settled,
      sprites: this.live.length,
      spritesPeak: this.spritesPeakN,
      started: { ...this.tweens.started },
      flashes: this.flashN,
      shots: { shown: this.shownN, skipped: this.skippedShotsN },
    };
  }

  resetPeaks(): void {
    this.tweens.resetPeak();
    this.particles.resetPeak();
    this.spritesPeakN = this.live.length;
  }
}
